import { formatCount } from '@jmc/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import {
  cx,
  EmptyState,
  ErrorState,
  LoadingBlock,
  Mono,
  PageHeader,
  Panel,
} from '../components/ui.js';

interface Room {
  roomCode: string;
  racks: number;
  files: number;
}
interface Rack {
  rackCode: string;
  columns: number;
  files: number;
}
interface Bag {
  id: number;
  columnCode: string;
  bagCode: string;
  files: number;
}

/**
 * Walks Room → Rack → Bag, mirroring how a clerk actually moves through the
 * record room. Each level shows how many files it holds, so an empty bag is
 * visible before anyone walks to it.
 */
export function RoomsPage() {
  const { t } = useI18n();
  const [room, setRoom] = useState<string | null>(null);
  const [rack, setRack] = useState<string | null>(null);

  const rooms = useQuery({ queryKey: ['rooms'], queryFn: () => api<Room[]>('/files/rooms') });

  const racks = useQuery({
    queryKey: ['racks', room],
    queryFn: () => api<Rack[]>(`/files/rooms/${encodeURIComponent(room!)}/racks`),
    enabled: Boolean(room),
  });

  const bags = useQuery({
    queryKey: ['bags', room, rack],
    queryFn: () =>
      api<Bag[]>(
        `/files/rooms/${encodeURIComponent(room!)}/racks/${encodeURIComponent(rack!)}/bags`,
      ),
    enabled: Boolean(room && rack),
  });

  return (
    <>
      <PageHeader
        title={t('locations.title')}
        subtitle={
          <nav className="flex flex-wrap items-center gap-1.5" aria-label="Breadcrumb">
            <button
              type="button"
              className={cx('hover:underline', !room && 'font-medium text-ink-900')}
              onClick={() => {
                setRoom(null);
                setRack(null);
              }}
            >
              {t('locations.title')}
            </button>
            {room && (
              <>
                <span aria-hidden="true">/</span>
                <button
                  type="button"
                  className={cx('hover:underline', !rack && 'font-medium text-ink-900')}
                  onClick={() => setRack(null)}
                >
                  {t('field.room')} {room}
                </button>
              </>
            )}
            {rack && (
              <>
                <span aria-hidden="true">/</span>
                <span className="font-medium text-ink-900">
                  {t('field.rack')} {rack}
                </span>
              </>
            )}
          </nav>
        }
      />

      {!room && (
        <>
          {rooms.isLoading && <LoadingBlock />}
          {rooms.isError && <ErrorState error={rooms.error} onRetry={() => void rooms.refetch()} />}
          {rooms.data?.length === 0 && <EmptyState title={t('common.none')} />}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {rooms.data?.map((entry) => (
              <button
                key={entry.roomCode}
                type="button"
                onClick={() => setRoom(entry.roomCode)}
                className="panel px-4 py-4 text-left transition-colors hover:border-navy-300"
              >
                <p className="text-[0.8125rem] text-ink-500">{t('field.room')}</p>
                <p className="mt-0.5 text-2xl font-semibold tabular">{entry.roomCode}</p>
                <p className="mt-2 text-[0.8125rem] text-ink-500 tabular">
                  {formatCount(entry.racks)}{' '}
                  {entry.racks === 1 ? t('locations.rack') : t('locations.racks')} ·{' '}
                  {formatCount(entry.files)}{' '}
                  {entry.files === 1 ? t('locations.file') : t('locations.files')}
                </p>
              </button>
            ))}
          </div>
        </>
      )}

      {room && !rack && (
        <>
          {racks.isLoading && <LoadingBlock />}
          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {racks.data?.map((entry) => (
              <button
                key={entry.rackCode}
                type="button"
                onClick={() => setRack(entry.rackCode)}
                className="panel px-3 py-3 text-left transition-colors hover:border-navy-300"
              >
                <p className="font-semibold tabular">{entry.rackCode}</p>
                <p className="mt-1 text-[0.75rem] text-ink-500 tabular">
                  {formatCount(entry.files)} {t('locations.files')}
                </p>
              </button>
            ))}
          </div>
        </>
      )}

      {room && rack && (
        <Panel bodyClassName="overflow-x-auto">
          {bags.isLoading && <LoadingBlock />}
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('field.column')}</th>
                <th>{t('field.bag')}</th>
                <th>{t('locations.files')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {bags.data?.map((bag) => (
                <tr key={bag.id}>
                  <td><Mono>{bag.columnCode}</Mono></td>
                  <td><Mono>{bag.bagCode}</Mono></td>
                  <td className="tabular">
                    {bag.files > 0 ? (
                      formatCount(bag.files)
                    ) : (
                      <span className="text-ink-400">{t('locations.emptyBag')}</span>
                    )}
                  </td>
                  <td className="text-right">
                    {bag.files > 0 && (
                      <Link
                        to={`/search?bagId=${bag.id}`}
                        className="text-navy-600 hover:underline"
                      >
                        {t('action.viewAll')}
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}
    </>
  );
}
